import ReactGA from 'react-ga4';

export interface GA4Config {
  measurementId: string;
  debug?: boolean;
  testMode?: boolean;
}

class GoogleAnalyticsService {
  private isInitialized = false;
  private config: GA4Config | null = null;
  private static readonly NOT_INITIALIZED_MESSAGE = 'Google Analytics not initialized';

  /**
   * Initialize Google Analytics 4
   * @param config - GA4 configuration
   */
  initialize(config: GA4Config): void {
    if (this.isInitialized) {
      console.warn('Google Analytics is already initialized');
      return;
    }

    if (!config.measurementId) {
      console.warn('Google Analytics measurement ID is required');
      return;
    }

    this.config = config;

    try {
      ReactGA.initialize(config.measurementId, {
        testMode: config.testMode || false,
      });

      this.isInitialized = true;
      console.log('Google Analytics initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Initialize Google Analytics 4 from environment variables
   */
  initializeFromEnv(): void {
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

    console.log('GA4 Debug Info:', {
      measurementId,
      nodeEnv: process.env.NODE_ENV,
      hasMeasurementId: !!measurementId,
    });

    if (!measurementId) {
      console.warn('REACT_APP_GA_MEASUREMENT_ID environment variable not set');
      return;
    }

    this.initialize({
      measurementId,
      debug: process.env.NODE_ENV === 'development',
      testMode: false,
    });
  }

  /**
   * Track page views
   * @param page - Page path
   * @param title - Page title
   * @param parameters - Additional parameters for the page view
   */
  trackPageView(page: string, title?: string, parameters?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn(GoogleAnalyticsService.NOT_INITIALIZED_MESSAGE);
      return;
    }

    try {
      ReactGA.send({
        hitType: 'pageview',
        page,
        title,
        ...parameters,
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom events
   * @param eventName - Event name
   * @param parameters - Event parameters
   */
  trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn(GoogleAnalyticsService.NOT_INITIALIZED_MESSAGE);
      return;
    }

    try {
      ReactGA.event(eventName, parameters);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track user login
   * @param method - Login method
   */
  trackLogin(method?: string): void {
    this.trackEvent('login', { method });
  }

  /**
   * Track user logout
   */
  trackLogout(): void {
    this.trackEvent('logout');
  }

  /**
   * Track user registration
   * @param method - Registration method
   */
  trackSignUp(method?: string): void {
    this.trackEvent('sign_up', { method });
  }

  /**
   * Track investment-related events
   * @param eventType - Type of investment event
   * @param parameters - Additional parameters
   */
  trackInvestmentEvent(eventType: string, parameters?: Record<string, any>): void {
    this.trackEvent(eventType, {
      ...parameters,
      event_category: 'investment',
    });
  }

  /**
   * Track fund opportunity views
   * @param opportunityId - Fund opportunity ID
   * @param opportunityName - Fund opportunity name
   */
  trackFundOpportunityView(opportunityId: string, opportunityName?: string): void {
    this.trackInvestmentEvent('view_fund_opportunity', {
      opportunity_id: opportunityId,
      opportunity_name: opportunityName,
    });
  }

  /**
   * Track fund opportunity interactions
   * @param opportunityId - Fund opportunity ID
   * @param action - Action taken (e.g., 'view_details', 'download_documents', 'submit_interest')
   */
  trackFundOpportunityAction(opportunityId: string, action: string): void {
    this.trackInvestmentEvent('fund_opportunity_action', {
      opportunity_id: opportunityId,
      action,
    });
  }

  /**
   * Track portfolio interactions
   * @param action - Portfolio action
   * @param parameters - Additional parameters
   */
  trackPortfolioAction(action: string, parameters?: Record<string, any>): void {
    this.trackInvestmentEvent('portfolio_action', {
      action,
      ...parameters,
    });
  }

  /**
   * Set user properties
   * @param properties - User properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn(GoogleAnalyticsService.NOT_INITIALIZED_MESSAGE);
      return;
    }

    try {
      ReactGA.set(properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Check if GA4 is initialized
   */
  getInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current configuration
   */
  getConfig(): GA4Config | null {
    return this.config;
  }

  /**
   * Flush all pending analytics requests and wait for completion
   * @param timeout - Maximum time to wait in milliseconds (default: 2000)
   */
  async flush(timeout: number = 2000): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      // Use ReactGA's gtag function to flush
      if (typeof window !== 'undefined' && (window as any).gtag) {
        return new Promise((resolve) => {
          const timeoutId = setTimeout(() => {
            console.warn('GA4 flush timeout reached');
            resolve();
          }, timeout);

          // Flush using gtag
          (window as any).gtag('event', 'page_view', {
            event_callback: () => {
              clearTimeout(timeoutId);
              resolve();
            },
          });
        });
      }
    } catch (error) {
      console.error('Failed to flush Google Analytics:', error);
    }
  }

  /**
   * Track user logout and wait for the request to complete
   * @param timeout - Maximum time to wait in milliseconds (default: 2000)
   */
  async trackLogoutAndFlush(timeout: number = 2000): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      // Track logout event
      this.trackEvent('logout');

      // Wait for the request to complete
      await this.flush(timeout);
    } catch (error) {
      console.error('Failed to track logout and flush:', error);
    }
  }
}

// Export singleton instance
export const googleAnalyticsService = new GoogleAnalyticsService();
export default googleAnalyticsService;
